import { LibraryToolbar, LIBRARY_TOOLBAR_CONTROL_CLASS } from '@/components/library/LibraryToolbar'
import { ReactNode } from 'react'
import { Check, ChevronDown, Filter, LayoutGrid, List } from '@digital-twins/design-system/icons'
import { useTranslation } from 'react-i18next'
import { KnowledgeFacetAggregate } from '@digital-twins/shared-types'
import { Button } from '@/components/ui/button'
import { CountryFlag } from '@/components/ui/CountryFlag'
import { Text } from '@/components/ui/Text'
import { ToggleChip } from '@/components/ui/toggle-chip'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  displayKnowledgeTypeLabel,
  knowledgeFacetAllLabel,
  knowledgeFacetKindLabel,
  marketDisplayName,
  marketFlagCode,
} from './libraryHelpers'

interface TypeTab {
  key: string
  label: string
  count: number
}

interface KnowledgeLibraryBandProps {
  typeTabs: Array<TypeTab>
  activeType: string | null
  onTypeChange: (type: string | null) => void
  facetCatalog: Array<KnowledgeFacetAggregate>
  filters: Record<string, string | null>
  onFilterChange: (kind: string, value: string | null) => void
  showFilterPanel: boolean
  onToggleFilterPanel: () => void
  resultCount: number | null
  /** Optional owner values derived from the document list (deterministic). */
  ownerOptions?: Array<{ value: string; count: number }>
  viewMode?: 'grid' | 'list'
  onViewModeChange?: (mode: 'grid' | 'list') => void
  /** Drawn after Add filter (Save filters). */
  filterActions?: ReactNode
  /** Search and Ask, drawn with the view toggle at the end of the row. */
  search?: ReactNode
}

const INLINE_KINDS = ['market', 'method'] as const
const PANEL_KINDS = ['year', 'source', 'category', 'brand', 'owner'] as const

function facetChipLabel(kind: string, value: string): string {
  if (kind === 'market') {
    return marketDisplayName(value)
  }
  if (kind === 'type') {
    return displayKnowledgeTypeLabel(value)
  }
  return value
}

export function FacetFilterPill({
  kind,
  label,
  agg,
  value,
  onChange,
  allLabel,
}: {
  kind: string
  label: string
  agg: KnowledgeFacetAggregate | undefined
  value: string | null
  onChange: (value: string | null) => void
  allLabel?: string
}) {
  const { t } = useTranslation('app')
  if (!agg || (agg.values.length === 0 && agg.unknownCount === 0)) {
    return null
  }
  const isOn = Boolean(value)
  const resolveDisplay = (v: string) => {
    if (kind === 'market') {
      return marketDisplayName(v)
    }
    if (kind === 'type') {
      return displayKnowledgeTypeLabel(v)
    }
    return v
  }
  const display =
    value === '__unknown__'
      ? t('library.unknown')
      : value
        ? resolveDisplay(value)
        : label

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          title={display}
          data-testid={`knowledge-facet-select-${kind}`}
          className={`${LIBRARY_TOOLBAR_CONTROL_CLASS} inline-flex max-w-[180px] min-w-0 items-center gap-1.5 overflow-hidden border transition-colors ${
            isOn
              ? 'border-primary/40 bg-primary/10 text-foreground'
              : 'border-border bg-transparent text-muted-foreground hover:bg-accent hover:text-foreground'
          }`}
        >
          <span className="truncate">{display}</span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-70" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-h-80 min-w-[180px] overflow-y-auto rounded-xl">
        <DropdownMenuItem
          data-testid={`knowledge-facet-option-${kind}-all`}
          onClick={() => onChange(null)}
          className="justify-between"
        >
          {allLabel ?? knowledgeFacetAllLabel(kind)}
          {!value ? <Check className="h-3.5 w-3.5 text-primary" /> : null}
        </DropdownMenuItem>
        {agg.values.map((v) => (
          <DropdownMenuItem
            key={v.value}
            data-testid={`knowledge-facet-option-${kind}-${v.value}`}
            onClick={() => onChange(v.value)}
            className="justify-between"
          >
            <span className="inline-flex min-w-0 items-center gap-1.5">
              {kind === 'market' ? <CountryFlag code={marketFlagCode(v.value)} /> : null}
              <span>
                {resolveDisplay(v.value)}{' '}
                <span className="text-muted-foreground">({v.count})</span>
              </span>
            </span>
            {value === v.value ? (
              <Check className="h-3.5 w-3.5 text-primary" />
            ) : null}
          </DropdownMenuItem>
        ))}
        {agg.unknownCount > 0 ? (
          <DropdownMenuItem
            data-testid={`knowledge-facet-option-${kind}-unknown`}
            onClick={() => onChange('__unknown__')}
            className="justify-between"
          >
            <span>
              {t('library.unknown')}{' '}
              <span className="text-muted-foreground">
                ({agg.unknownCount})
              </span>
            </span>
            {value === '__unknown__' ? (
              <Check className="h-3.5 w-3.5 text-primary" />
            ) : null}
          </DropdownMenuItem>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export function KnowledgeLibraryBand({
  typeTabs,
  activeType,
  onTypeChange,
  facetCatalog,
  filters,
  onFilterChange,
  showFilterPanel,
  onToggleFilterPanel,
  resultCount,
  ownerOptions = [],
  viewMode = 'grid',
  onViewModeChange,
  filterActions,
  search,
}: KnowledgeLibraryBandProps) {
  const { t } = useTranslation('app')
  function valuesFor(kind: string): KnowledgeFacetAggregate | undefined {
    if (kind === 'owner') {
      return {
        kind: 'owner',
        displayName: knowledgeFacetKindLabel('owner'),
        values: ownerOptions.map((o) => ({
          value: o.value,
          count: o.count,
        })),
        unknownCount: 0,
      }
    }
    return facetCatalog.find((f) => f.kind === kind)
  }

  // Only the panel's filters: Type, Market and Method show their value on their own pill.
  const activeFilterCount = PANEL_KINDS.filter((kind) => Boolean(filters[kind])).length

  return (
    <div className="space-y-3" data-testid="knowledge-library-band">
      <div data-testid="knowledge-inline-facets">
        <LibraryToolbar
          filters={<>
            <FacetFilterPill
              kind="type"
              label={knowledgeFacetKindLabel('type')}
              agg={{
                kind: 'type',
                displayName: knowledgeFacetKindLabel('type'),
                values: typeTabs
                  .filter((tab) => tab.key !== 'all')
                  .map((tab) => ({ value: tab.key, count: tab.count })),
                unknownCount: 0,
              }}
              value={activeType}
              onChange={onTypeChange}
            />
            {INLINE_KINDS.map((kind) => {
              const agg = valuesFor(kind)
              return (
                <FacetFilterPill
                  key={kind}
                  kind={kind}
                  label={knowledgeFacetKindLabel(kind)}
                  agg={agg}
                  value={filters[kind] ?? null}
                  onChange={(value) => onFilterChange(kind, value)}
                />
              )
            })}
            <Button
              type="button"
              variant="outline"
              size="sm"
              shape="pill"
              data-testid="knowledge-add-filter"
              className={`${LIBRARY_TOOLBAR_CONTROL_CLASS} border-dashed font-normal text-muted-foreground`}
              onClick={onToggleFilterPanel}
            >
              <Filter className="mr-1.5 h-3.5 w-3.5" />
              {activeFilterCount > 0
                ? t('library.addFilterCount', { count: activeFilterCount })
                : t('library.addFilter')}
            </Button>
            {filterActions}
          </>}
          search={search}
          actions={<>
            {onViewModeChange ? (
            <div
              className="inline-flex h-12 overflow-hidden rounded-xl border border-border"
              data-testid="knowledge-view-toggle"
              role="group"
              aria-label={t('library.viewAria')}
            >
              <button
                type="button"
                className={`flex h-full w-11 items-center justify-center ${
                  viewMode === 'grid'
                    ? 'bg-muted text-foreground'
                    : 'text-muted-foreground hover:bg-accent'
                }`}
                data-testid="knowledge-view-grid"
                aria-label={t('library.viewGrid')}
                aria-pressed={viewMode === 'grid'}
                onClick={() => onViewModeChange('grid')}
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
              <button
                type="button"
                className={`flex h-full w-11 items-center justify-center border-l border-border ${
                  viewMode === 'list'
                    ? 'bg-muted text-foreground'
                    : 'text-muted-foreground hover:bg-accent'
                }`}
                data-testid="knowledge-view-list"
                aria-label={t('library.viewList')}
                aria-pressed={viewMode === 'list'}
                onClick={() => onViewModeChange('list')}
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          ) : null}
          </>}
        />
      </div>

      {showFilterPanel ? (
        <div
          className="flex min-w-0 flex-wrap gap-x-10 gap-y-5 overflow-hidden rounded-2xl border border-border bg-card-inset p-5 dark:bg-card"
          data-testid="knowledge-filter-panel"
        >
          {PANEL_KINDS.map((kind) => {
            const agg = valuesFor(kind)
            if (!agg) return null
            return (
              <div key={kind} className="min-w-0 max-w-full">
                <Text size="xs" color="muted" className="mb-2.5">
                  {knowledgeFacetKindLabel(kind)}
                </Text>
                <div className="flex min-w-0 flex-wrap gap-1.5">
                  {agg.values.map((v) => (
                    <ToggleChip
                      key={v.value}
                      size="sm"
                      variant="outline"
                      selected={filters[kind] === v.value}
                      title={`${facetChipLabel(kind, v.value)} (${v.count})`}
                      className="max-w-full min-w-0 overflow-hidden rounded-[6px]"
                      data-testid={`knowledge-filter-chip-${kind}-${v.value}`}
                      onClick={() =>
                        onFilterChange(
                          kind,
                          filters[kind] === v.value ? null : v.value,
                        )
                      }
                    >
                      <span className="truncate">
                        {facetChipLabel(kind, v.value)} ({v.count})
                      </span>
                    </ToggleChip>
                  ))}
                  {agg.unknownCount > 0 ? (
                    <ToggleChip
                      size="sm"
                      variant="outline"
                      selected={filters[kind] === '__unknown__'}
                      className="max-w-full min-w-0 overflow-hidden rounded-[6px]"
                      data-testid={`knowledge-filter-chip-${kind}-unknown`}
                      onClick={() =>
                        onFilterChange(
                          kind,
                          filters[kind] === '__unknown__'
                            ? null
                            : '__unknown__',
                        )
                      }
                    >
                      <span className="truncate">
                        {t('library.unknown')} ({agg.unknownCount})
                      </span>
                    </ToggleChip>
                  ) : null}
                </div>
              </div>
            )
          })}
        </div>
      ) : null}

      {resultCount != null ? (
        <Text size="sm" color="muted" data-testid="knowledge-result-count">
          {t('library.result', { count: resultCount })}
        </Text>
      ) : null}
    </div>
  )
}
